export interface HiddenColumnPreference {
  mode: 'hidden-blacklist';
  version: number;
  hiddenColumnKeys: string[];
}

export const HIDDEN_COLUMN_PREFERENCE_VERSION = 1;

function uniquePreservingOrder<T extends string>(values: readonly T[]): T[] {
  return Array.from(new Set(values));
}

export function sanitizeVisibleColumnKeys<T extends string>(
  columnKeys: readonly string[] | undefined,
  allowedKeys: readonly T[],
  fallbackVisibleKeys?: readonly T[],
): T[] {
  if (allowedKeys.length === 0) {
    return [];
  }

  const allowedKeySet = new Set<string>(allowedKeys);
  const sanitizedKeys = uniquePreservingOrder(
    (columnKeys || []).filter((columnKey): columnKey is T => allowedKeySet.has(columnKey)),
  );

  if (sanitizedKeys.length > 0) {
    return sanitizedKeys;
  }

  if (fallbackVisibleKeys && fallbackVisibleKeys.length > 0) {
    const sanitizedFallback = uniquePreservingOrder(
      fallbackVisibleKeys.filter((columnKey): columnKey is T => allowedKeySet.has(columnKey)),
    );
    if (sanitizedFallback.length > 0) {
      return sanitizedFallback;
    }
  }

  return [...allowedKeys];
}

export function sanitizeHiddenColumnKeys<T extends string>(
  columnKeys: readonly string[] | undefined,
  allowedKeys: readonly T[],
): T[] {
  if (allowedKeys.length === 0) {
    return [];
  }

  const allowedKeySet = new Set<string>(allowedKeys);
  return uniquePreservingOrder(
    (columnKeys || []).filter((columnKey): columnKey is T => allowedKeySet.has(columnKey)),
  ).sort();
}

export function getHiddenColumnKeysFromVisibleColumns<T extends string>(
  visibleColumnKeys: readonly string[] | undefined,
  allowedKeys: readonly T[],
  fallbackVisibleKeys?: readonly T[],
): T[] {
  const sanitizedVisibleColumnKeys = new Set(
    sanitizeVisibleColumnKeys(visibleColumnKeys, allowedKeys, fallbackVisibleKeys),
  );

  return sanitizeHiddenColumnKeys(
    allowedKeys.filter((columnKey) => !sanitizedVisibleColumnKeys.has(columnKey)),
    allowedKeys,
  );
}

export function getVisibleColumnKeysFromHiddenColumns<T extends string>(
  hiddenColumnKeys: readonly string[] | undefined,
  allowedKeys: readonly T[],
  fallbackVisibleKeys?: readonly T[],
): T[] {
  if (allowedKeys.length === 0) {
    return [];
  }

  const hiddenColumnKeySet = new Set(sanitizeHiddenColumnKeys(hiddenColumnKeys, allowedKeys));
  const visibleColumnKeys = allowedKeys.filter((columnKey) => !hiddenColumnKeySet.has(columnKey));

  return sanitizeVisibleColumnKeys(visibleColumnKeys, allowedKeys, fallbackVisibleKeys);
}

export function buildHiddenColumnPreference<T extends string>(
  visibleColumnKeys: readonly string[] | undefined,
  allowedKeys: readonly T[],
  fallbackVisibleKeys?: readonly T[],
): HiddenColumnPreference {
  return {
    mode: 'hidden-blacklist',
    version: HIDDEN_COLUMN_PREFERENCE_VERSION,
    hiddenColumnKeys: getHiddenColumnKeysFromVisibleColumns(visibleColumnKeys, allowedKeys, fallbackVisibleKeys),
  };
}

export function resolveVisibleColumnsFromStoredPreference<T extends string>(
  storedValue: unknown,
  allowedKeys: readonly T[],
  defaultVisibleKeys?: readonly T[],
): T[] {
  if (Array.isArray(storedValue)) {
    return sanitizeVisibleColumnKeys(storedValue, allowedKeys, defaultVisibleKeys);
  }

  if (storedValue && typeof storedValue === 'object') {
    const record = storedValue as Partial<HiddenColumnPreference> & { hiddenColumns?: unknown };
    const hiddenColumnKeys = Array.isArray(record.hiddenColumnKeys)
      ? record.hiddenColumnKeys
      : Array.isArray(record.hiddenColumns)
        ? record.hiddenColumns
        : null;

    if (hiddenColumnKeys) {
      return getVisibleColumnKeysFromHiddenColumns(hiddenColumnKeys, allowedKeys, defaultVisibleKeys);
    }
  }

  return sanitizeVisibleColumnKeys(defaultVisibleKeys, allowedKeys, defaultVisibleKeys);
}

export function resolveVisibleColumnsFromSerializedPreferenceString<T extends string>(
  value: string | null,
  allowedKeys: readonly T[],
  defaultVisibleKeys?: readonly T[],
): T[] {
  if (!value) {
    return sanitizeVisibleColumnKeys(defaultVisibleKeys, allowedKeys, defaultVisibleKeys);
  }

  try {
    const parsedValue = JSON.parse(value) as unknown;
    return resolveVisibleColumnsFromStoredPreference(parsedValue, allowedKeys, defaultVisibleKeys);
  } catch {
    return sanitizeVisibleColumnKeys(defaultVisibleKeys, allowedKeys, defaultVisibleKeys);
  }
}